import type { TanstackReactTable } from "@devhop/ui";
import { Button, confirm, DataTable, toast } from "@devhop/ui";
import { DownloadIcon, TrashIcon } from "lucide-react";
import { FORM_RESPONSE_SIMILARITY_THRESHOLD_PERCENT } from "@/modules/forms/domain/lib/form-response-similarity";
import { useCallback, useState } from "react";
import { formatFormAnswerDisplay } from "@/modules/forms/domain/lib/format-form-answer-display";
import { adminFormResponsesApi } from "@/modules/forms/presentation/api/client";
import type { FormResponsesApi } from "@/modules/forms/presentation/api/form-responses-api";
import type {
  FormFieldDTO,
  FormResponsesListDTO,
} from "@/modules/forms/presentation/api/types";
import { formatDateTime } from "@/shared/lib/date-time";

type Row = FormResponsesListDTO["data"][number];

function fileBasename(fileKey: string): string {
  const i = fileKey.lastIndexOf("/");
  return i >= 0 ? fileKey.slice(i + 1) : fileKey;
}

function FileAnswerDownloadCell({
  formId,
  fileKey,
  api,
}: {
  formId: string;
  fileKey: string;
  api: FormResponsesApi;
}) {
  const [busy, setBusy] = useState(false);
  const label = fileBasename(fileKey);

  const onDownload = useCallback(async () => {
    setBusy(true);
    try {
      const { url } = await api.getAnswerFileReadUrl(formId, fileKey);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("ດາວນໂຫຼດບໍ່ສຳເລັດ");
    } finally {
      setBusy(false);
    }
  }, [api, formId, fileKey]);

  return (
    <div className="flex max-w-[18rem] flex-col gap-1.5">
      <span
        className="truncate font-mono text-muted-foreground text-xs"
        title={fileKey}
      >
        {label}
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-fit gap-1.5"
        disabled={busy}
        onClick={() => void onDownload()}
      >
        <DownloadIcon className="size-4 shrink-0" aria-hidden />
        {busy ? "ກຳລັງເປີດ…" : "ດາວນໂຫຼດ"}
      </Button>
    </div>
  );
}

type FormResponsesDataTableProps = {
  formId: string;
  fields: FormFieldDTO[];
  data: Row[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  responsesApi?: FormResponsesApi;
  canDeleteSimilar?: boolean;
  onDeleteSimilar?: (responseId: string) => void | Promise<void>;
  deletePending?: boolean;
};

export function FormResponsesDataTable({
  formId,
  fields,
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  responsesApi = adminFormResponsesApi,
  canDeleteSimilar = false,
  onDeleteSimilar,
  deletePending = false,
}: FormResponsesDataTableProps) {
  const dataFields = [...fields]
    .filter((f) => f.type !== "section")
    .sort((a, b) => a.order - b.order);

  const columns: TanstackReactTable.ColumnDef<Row>[] = [
    {
      id: "submitted",
      header: "ເວລາສົ່ງ",
      accessorKey: "submittedAt",
      enableSorting: false,
      cell: ({ row }) => formatDateTime(row.original.submittedAt ?? null),
    },
    {
      id: "similarity",
      header: "ຄວາມຄ້າຍຄື",
      enableSorting: false,
      size: 96,
      cell: ({ row }) => {
        const pct = row.original.maxSimilarityPercent ?? 0;
        if (pct < FORM_RESPONSE_SIMILARITY_THRESHOLD_PERCENT) {
          return <span className="text-muted-foreground text-sm">—</span>;
        }
        return (
          <span className="font-mono text-sm tabular-nums">{pct}%</span>
        );
      },
    },
    ...dataFields.map(
      (f): TanstackReactTable.ColumnDef<Row> => ({
        id: f.id,
        header: f.label,
        enableSorting: false,
        size: 180,
        cell: ({ row }) => {
          const cellData = row.original.answers[f.id];
          if (f.type === "file" && cellData?.fileKey) {
            return (
              <FileAnswerDownloadCell
                formId={formId}
                fileKey={cellData.fileKey}
                api={responsesApi}
              />
            );
          }
          return (
            <div className="max-w-[16rem] truncate text-sm">
              {formatFormAnswerDisplay(cellData, {
                type: f.type,
                options: f.options,
                settings: f.settings,
              })}
            </div>
          );
        },
      }),
    ),
    {
      id: "actions",
      header: "",
      enableSorting: false,
      size: 96,
      cell: ({ row }) => {
        if (!canDeleteSimilar || !onDeleteSimilar) return null;
        const pct = row.original.maxSimilarityPercent ?? 0;
        if (pct < FORM_RESPONSE_SIMILARITY_THRESHOLD_PERCENT) return null;

        const isDuplicateRow = row.original.canDeleteAsSimilarDuplicate;

        const runDelete = async (title: string, description: string) => {
          const ok = await confirm({
            title,
            description,
            actionText: "ລຶບ",
            ActionProps: { variant: "destructive" },
          });
          if (ok) await onDeleteSimilar(row.original.id);
        };

        if (isDuplicateRow) {
          return (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1 text-destructive hover:text-destructive"
              disabled={deletePending}
              onClick={() =>
                void runDelete(
                  "ລຶບຄຳຕອບຊ້ຳ",
                  `ຄວາມຄ້າຍຄື ${pct}% — ລຶບຕົວຊ້ຳ (ເກັບແຖວສົ່ງເກົ່າສຸດໃນກຸ່ມ). ກູ້ຄືນບໍ່ໄດ້.`,
                )
              }
            >
              <TrashIcon className="size-4" aria-hidden />
              ລຶບຊ້ຳ
            </Button>
          );
        }

        return (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="gap-1 text-muted-foreground hover:text-destructive"
            disabled={deletePending}
            onClick={() =>
              void runDelete(
                "ລຶບຄຳຕອບ",
                `ຄວາມຄ້າຍຄື ${pct}% (≥ ${FORM_RESPONSE_SIMILARITY_THRESHOLD_PERCENT}%). ກູ້ຄືນບໍ່ໄດ້.`,
              )
            }
          >
            <TrashIcon className="size-3.5" aria-hidden />
            ລຶບ
          </Button>
        );
      },
    },
  ];

  return (
    <DataTable<Row, unknown>
      noDataMessage="ຍັງບໍ່ມີຄຳຕອບ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      sortBy="submittedAt"
      sortOrder="desc"
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      onSortingChange={() => {}}
    />
  );
}
